import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, SparklesIcon } from 'lucide-react';
export function Pricing() {
  return <section id="pricing" className="relative w-full py-32 lg:py-40 overflow-hidden bg-gradient-to-b from-midnight-deep to-midnight-light" aria-labelledby="pricing-heading" data-id="element-98">
      {/* Background accents */}
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-amber/[0.02] blur-[120px]" data-id="element-99" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-100">
        {/* Section header */}
        <motion.div className="text-center mb-20" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-101">
          <p className="font-sora text-amber text-sm font-semibold tracking-[0.2em] uppercase mb-4" data-id="element-102">
            Pricing
          </p>
          <h2 id="pricing-heading" className="font-sora text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-103">
            Invest in Your Core Loop
          </h2>
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto" data-id="element-104">
            Choose the plan that matches your studio's ambition. All plans
            include a dedicated onboarding session.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch" data-id="element-105">
          {/* Ember */}
          <motion.article className="relative flex flex-col p-8 lg:p-10 rounded-2xl bg-midnight-lighter/30 backdrop-blur-xl border border-white/5" initial={{
          opacity: 0,
          y: 60
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7,
          delay: 0,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-106">
            <div className="mb-8" data-id="element-107">
              <h3 className="font-sora text-2xl font-bold text-white mb-2" data-id="element-108">
                Ember
              </h3>
              <p className="font-inter text-text-muted text-sm mb-6" data-id="element-109">
                For studios testing the waters
              </p>
              <div className="flex items-baseline gap-1" data-id="element-110">
                <span className="font-sora text-4xl lg:text-5xl font-bold text-white" data-id="element-111">
                  €2,750
                </span>
                <span className="font-inter text-text-muted" data-id="element-112">/mo</span>
              </div>
            </div>

            <ul className="space-y-4 mb-10 flex-1" role="list" data-id="element-113">
              <li className="flex items-start gap-3" data-id="element-114">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-115" />
                <span className="font-inter text-text-secondary" data-id="element-116">
                  1 game loop analysis per month
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-117">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-118" />
                <span className="font-inter text-text-secondary" data-id="element-119">
                  Basic pattern library access
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-120">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-121" />
                <span className="font-inter text-text-secondary" data-id="element-122">
                  Email support
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-123">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-124" />
                <span className="font-inter text-text-secondary" data-id="element-125">
                  Standard reporting
                </span>
              </li>
            </ul>

            <a href="#" className="inline-flex items-center justify-center w-full px-6 py-4 border border-white/20 hover:border-white/40 text-white font-inter font-semibold text-base rounded-lg transition-all duration-300 hover:bg-white/5" data-id="element-126">
              Start with Ember
            </a>
          </motion.article>

          {/* Forge — Featured */}
          <motion.article className="relative flex flex-col p-8 lg:p-10 rounded-2xl bg-midnight-lighter/50 backdrop-blur-xl border border-amber/30 lg:scale-105 shadow-[0_0_60px_rgba(245,166,35,0.08)]" initial={{
          opacity: 0,
          y: 60
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-127">
            {/* Popular badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-128">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-amber text-midnight-deep font-sora font-semibold text-xs tracking-wider uppercase rounded-full" data-id="element-129">
                <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-130" />
                Most Popular
              </span>
            </div>

            <div className="mb-8 mt-2" data-id="element-131">
              <h3 className="font-sora text-2xl font-bold text-white mb-2" data-id="element-132">
                Forge
              </h3>
              <p className="font-inter text-text-muted text-sm mb-6" data-id="element-133">
                For studios scaling fast
              </p>
              <div className="flex items-baseline gap-1" data-id="element-134">
                <span className="font-sora text-4xl lg:text-5xl font-bold text-amber" data-id="element-135">
                  €7,200
                </span>
                <span className="font-inter text-text-muted" data-id="element-136">/mo</span>
              </div>
            </div>

            <ul className="space-y-4 mb-10 flex-1" role="list" data-id="element-137">
              <li className="flex items-start gap-3" data-id="element-138">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-139" />
                <span className="font-inter text-text-secondary" data-id="element-140">
                  5 game loop analyses per month
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-141">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-142" />
                <span className="font-inter text-text-secondary" data-id="element-143">
                  Full pattern library + behavioral simulation
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-144">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-145" />
                <span className="font-inter text-text-secondary" data-id="element-146">
                  Priority support + dedicated CSM
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-147">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-148" />
                <span className="font-inter text-text-secondary" data-id="element-149">
                  Advanced analytics dashboard
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-150">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-151" />
                <span className="font-inter text-text-secondary" data-id="element-152">
                  Custom reward modeling
                </span>
              </li>
            </ul>

            <a href="#" className="inline-flex items-center justify-center w-full px-6 py-4 bg-amber hover:bg-amber-hover text-midnight-deep font-inter font-semibold text-base rounded-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(245,166,35,0.3)]" data-id="element-153">
              Choose Forge
            </a>
          </motion.article>

          {/* Crucible */}
          <motion.article className="relative flex flex-col p-8 lg:p-10 rounded-2xl bg-midnight-lighter/30 backdrop-blur-xl border border-white/5" initial={{
          opacity: 0,
          y: 60
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7,
          delay: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-154">
            <div className="mb-8" data-id="element-155">
              <h3 className="font-sora text-2xl font-bold text-white mb-2" data-id="element-156">
                Crucible
              </h3>
              <p className="font-inter text-text-muted text-sm mb-6" data-id="element-157">
                For enterprises reshaping the industry
              </p>
              <div className="flex items-baseline gap-1" data-id="element-158">
                <span className="font-sora text-4xl lg:text-5xl font-bold text-white" data-id="element-159">
                  Custom
                </span>
              </div>
            </div>

            <ul className="space-y-4 mb-10 flex-1" role="list" data-id="element-160">
              <li className="flex items-start gap-3" data-id="element-161">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-162" />
                <span className="font-inter text-text-secondary" data-id="element-163">
                  Unlimited analyses
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-164">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-165" />
                <span className="font-inter text-text-secondary" data-id="element-166">
                  Full platform access + API
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-167">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-168" />
                <span className="font-inter text-text-secondary" data-id="element-169">
                  24/7 dedicated support
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-170">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-171" />
                <span className="font-inter text-text-secondary" data-id="element-172">
                  White-label options
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-173">
                <CheckIcon className="w-5 h-5 text-amber mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-174" />
                <span className="font-inter text-text-secondary" data-id="element-175">
                  On-premise deployment available
                </span>
              </li>
            </ul>

            <a href="#" className="inline-flex items-center justify-center w-full px-6 py-4 border border-white/20 hover:border-white/40 text-white font-inter font-semibold text-base rounded-lg transition-all duration-300 hover:bg-white/5" data-id="element-176">
              Contact Sales
            </a>
          </motion.article>
        </div>
      </div>
    </section>;
}